import { Injectable, inject } from '@angular/core';
import {registerLocaleData} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeEn from '@angular/common/locales/en';
import localeEnExtra from '@angular/common/locales/extra/en';
import localeFr from '@angular/common/locales/fr';
import localeFrExtra from '@angular/common/locales/extra/fr';
import {TranslateService} from '@ngx-translate/core';

registerLocaleData(localeDe, localeDeExtra);
registerLocaleData(localeEn, localeEnExtra);
registerLocaleData(localeFr, localeFrExtra);

@Injectable({
  providedIn: 'root'
})
export class LocaleService {
  private static readonly storageKey = 'birco.language';

  private _translateService = inject(TranslateService);

  private readonly _supportedLanguages = ['de', 'en', 'fr'];
  private readonly _defaultLanguage = this._supportedLanguages[0];
  private _userLanguage: string;

  constructor() {
    this._userLanguage = this.resolveInitialLanguage();
    this.initLanguage();
  }

  get userLanguage(): string {
    return this._userLanguage;
  }

  get defaultLanguage(): string {
    return this._defaultLanguage;
  }

  get supportedLanguages(): string[] {
    return this._supportedLanguages;
  }

  initLanguage(): void {
    this._translateService.setDefaultLang(this._defaultLanguage);
    this._translateService.use(this.userLanguage);
    document.documentElement.lang = this.userLanguage;
  }

  setLanguage(language: string): boolean {
    if (!this.isSupportedLanguage(language) || language === this._userLanguage) {
      return false;
    }

    this._userLanguage = language;
    localStorage.setItem(LocaleService.storageKey, language);
    this._translateService.use(language);
    document.documentElement.lang = language;
    return true;
  }

  private resolveInitialLanguage(): string {
    const storedLanguage = localStorage.getItem(LocaleService.storageKey);
    if (this.isSupportedLanguage(storedLanguage)) {
      return storedLanguage;
    }

    const browserLanguage = navigator.language.substring(0, 2);
    if (this.isSupportedLanguage(browserLanguage)) {
      return browserLanguage;
    }

    return this._defaultLanguage;
  }

  private isSupportedLanguage(language: string | null): language is string {
    return !!language && this._supportedLanguages.includes(language);
  }
}
